import React, { useCallback, useEffect, useId } from 'react';

import { useIntl } from 'react-intl';

import { Col, Form, Input, Row } from 'antd';

import WorkJourneyFormSection from '@src/packages/workJourney/components/WorkJourneyFormSection';
import { JOURNEY_ASSIGNED_USERS_TYPE } from '@src/packages/workJourney/constants';
import { useWorkJourneyContext } from '@src/packages/workJourney/contexts';
import type { WorkJourneyAssignedUser, WorkJourneyForm } from '@src/packages/workJourney/types';

import layoutStyles from '../../../../components/workJourneyFormLayout.module.less';
import JourneyAssignedUsers from '../JourneyAssignedUsers';
import CollationSelector from './components/CollationSelector';
import EarlyTerminationSelector from './components/EarlyTerminationSelector';
import JourneyStateSelector from './components/JourneyStateSelector';
import PlaceSelector from './components/PlaceSelector';
import RequiredMembersSelector from './components/RequiredMembersSelector';
import ShiftJourneySelector from './components/ShiftJourneySelector';
import TolerenceSelector from './components/TolerenceSelector';

/**
 * @description Form component for creating different types of work journeys
 */
const JourneyForm = () => {
  const { workJourneyForm, setWorkJourneyForm } = useWorkJourneyContext();
  const [form] = Form.useForm();
  const intl = useIntl();
  const formInstanceId = useId();

  const journeyDataSectionId = `${formInstanceId}-journey-data`;
  const shiftSectionId = `${formInstanceId}-shift`;
  const collationSectionId = `${formInstanceId}-collation`;
  const placeSectionId = `${formInstanceId}-place`;
  const toleranceSectionId = `${formInstanceId}-tolerance`;
  const earlyTerminationSectionId = `${formInstanceId}-early-termination`;
  const supervisorsSectionId = `${formInstanceId}-supervisors`;
  const requiredMembersSectionId = `${formInstanceId}-required-members`;
  const assignedUsersSectionId = `${formInstanceId}-assigned-users`;
  const stateSectionId = `${formInstanceId}-state`;

  useEffect(() => {
    form.setFieldsValue({ name: workJourneyForm.name });
  }, [form, workJourneyForm.name]);

  const handleValuesChange = useCallback(
    (changedValues: Partial<WorkJourneyForm>) => {
      setWorkJourneyForm(prev => ({ ...prev, ...changedValues }));
    },
    [setWorkJourneyForm],
  );

  const removeSupervisor = useCallback(
    (user: WorkJourneyAssignedUser) => {
      setWorkJourneyForm(prev => ({
        ...prev,
        supervisors: prev.supervisors.filter(s => s?.user?.id !== user?.user?.id),
      }));
    },
    [setWorkJourneyForm],
  );

  const removeUser = useCallback(
    (user: WorkJourneyAssignedUser) => {
      setWorkJourneyForm(prev => ({
        ...prev,
        members: prev.members.filter(u => u?.user?.id !== user?.user?.id),
      }));
    },
    [setWorkJourneyForm],
  );

  const addSupervisors = useCallback(
    (users: WorkJourneyAssignedUser[]) => {
      setWorkJourneyForm(prev => {
        const existingIds = prev.supervisors.map(s => s?.user?.id);
        const newSupervisors = users.filter(user => !existingIds.includes(user?.user?.id));
        return { ...prev, supervisors: [...prev.supervisors, ...newSupervisors] };
      });
    },
    [setWorkJourneyForm],
  );

  const addUsers = useCallback(
    (users: WorkJourneyAssignedUser[]) => {
      setWorkJourneyForm(prev => {
        const existingIds = prev.members.map(m => m?.user?.id);
        const newMembers = users.filter(user => !existingIds.includes(user?.user?.id));
        return { ...prev, members: [...prev.members, ...newMembers] };
      });
    },
    [setWorkJourneyForm],
  );

  return (
    <div className={layoutStyles.journeyFormContainer}>
      <Form
        form={form}
        layout="vertical"
        colon={false}
        initialValues={workJourneyForm}
        onValuesChange={handleValuesChange}
      >
        <WorkJourneyFormSection
          titleId={journeyDataSectionId}
          titleMessageId="workJourney.create.journeyData"
        >
          <Row className={layoutStyles.formSectionRow}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label={intl.formatMessage({ id: 'workJourney.create.journeyName' })}
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: 'workJourney.create.journeyNameRequired' }),
                  },
                ]}
              >
                <Input
                  placeholder={intl.formatMessage({
                    id: 'workJourney.create.journeyNamePlaceholder',
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
        </WorkJourneyFormSection>

        <WorkJourneyFormSection
          titleId={shiftSectionId}
          titleMessageId="workJourney.create.associatedShift"
        >
          <ShiftJourneySelector form={form} />
        </WorkJourneyFormSection>

        <WorkJourneyFormSection
          titleId={collationSectionId}
          titleMessageId="workJourney.mealBreakMarking"
        >
          <CollationSelector form={form} />
        </WorkJourneyFormSection>

        <WorkJourneyFormSection titleId={placeSectionId} titleMessageId="workJourney.location">
          <PlaceSelector form={form} />
        </WorkJourneyFormSection>

        <WorkJourneyFormSection
          titleId={toleranceSectionId}
          titleMessageId="workJourney.scheduleTolerance"
        >
          <TolerenceSelector />
        </WorkJourneyFormSection>

        <WorkJourneyFormSection
          titleId={earlyTerminationSectionId}
          titleMessageId="workJourney.earlyTerminationJourney"
        >
          <EarlyTerminationSelector />
        </WorkJourneyFormSection>

        <WorkJourneyFormSection
          titleId={supervisorsSectionId}
          titleMessageId="workJourney.assignedSupervisors"
          descriptionMessageId="workJourney.create.selectSupervisors"
        >
          <JourneyAssignedUsers
            users={workJourneyForm.supervisors}
            removeUser={removeSupervisor}
            addUsers={addSupervisors}
            type={JOURNEY_ASSIGNED_USERS_TYPE.SUPERVISOR}
          />
        </WorkJourneyFormSection>

        <WorkJourneyFormSection
          titleId={requiredMembersSectionId}
          titleMessageId="workJourney.requiredUsers"
        >
          <RequiredMembersSelector form={form} />
        </WorkJourneyFormSection>

        <WorkJourneyFormSection
          titleId={assignedUsersSectionId}
          titleMessageId="workJourney.assignedUsers"
          descriptionMessageId="workJourney.create.selectUsers"
        >
          <JourneyAssignedUsers
            users={workJourneyForm.members}
            removeUser={removeUser}
            addUsers={addUsers}
            type={JOURNEY_ASSIGNED_USERS_TYPE.MEMBER}
          />
        </WorkJourneyFormSection>

        <WorkJourneyFormSection
          titleId={stateSectionId}
          titleMessageId="workJourney.journeyState"
          showDivider={false}
        >
          <JourneyStateSelector action="create" />
        </WorkJourneyFormSection>
      </Form>
    </div>
  );
};

export default JourneyForm;
